import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Play,
  History,
  Download,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Eye,
  X
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function SimulationTrace() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [simulations, setSimulations] = useState([]);
  const [currentSimulation, setCurrentSimulation] = useState(null);
  const [expandedStock, setExpandedStock] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [stats, setStats] = useState({
    totalStocks: 0,
    totalRuntime: 0,
    avgConfidence: 0,
    successfulModules: 0,
    failedModules: 0,
    lastSimTime: null
  });

  useEffect(() => {
    fetchSimulations();
  }, []);

  const fetchSimulations = async () => {
    try {
      const res = await axios.get(`${API}/api/ai/simulation/list?limit=20`);
      setSimulations(res.data.simulations || []);
      
      // Calculate stats from latest simulation
      if (res.data.simulations && res.data.simulations.length > 0) {
        const latest = res.data.simulations[0];
        setStats({
          totalStocks: latest.total_stocks || 0,
          totalRuntime: latest.total_runtime_ms || 0,
          avgConfidence: latest.avg_confidence || 0,
          successfulModules: latest.successful_modules || 0,
          failedModules: latest.failed_modules || 0,
          lastSimTime: latest.end_time
        });
      }
    } catch (err) {
      console.error("Error fetching simulations:", err);
    }
  };

  const runSimulation = async (type = "full") => {
    setLoading(true);
    setCurrentSimulation(null);
    
    try {
      toast.info("Starting simulation...");
      
      const res = await axios.post(`${API}/api/ai/simulation/run`, {
        simulation_type: type
      });
      
      setCurrentSimulation(res.data);
      toast.success(`Simulation completed! ${res.data.total_stocks} stocks analyzed`);
      
      // Refresh list
      fetchSimulations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Simulation failed");
      console.error("Simulation error:", err);
    } finally {
      setLoading(false);
    }
  };

  const viewSimulationDetails = async (simulationId) => {
    try {
      const res = await axios.get(`${API}/api/ai/simulation/${simulationId}`);
      setCurrentSimulation(res.data);
    } catch (err) {
      toast.error("Failed to load simulation details");
      console.error(err);
    }
  };

  const exportToExcel = async (simulationId) => {
    try {
      toast.info("Exporting to Excel...");
      
      const res = await axios.get(`${API}/api/ai/simulation/${simulationId}/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `simulation_${simulationId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Excel exported successfully!");
    } catch (err) {
      toast.error("Export failed");
      console.error(err);
    }
  };

  const toggleStockDetails = (symbol) => {
    setExpandedStock(expandedStock === symbol ? null : symbol);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Deep Simulation Trace Engine
            </h1>
            <p className="text-gray-400 mt-2">Audit, replay, and explain existing StockSense production logic</p>
          </div>
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            ← Back to Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Stats Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-gray-800/50 border-gray-700 p-4">
            <div className="text-gray-400 text-sm">Total Stocks</div>
            <div className="text-2xl font-bold text-blue-400 mt-1">{stats.totalStocks}</div>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700 p-4">
            <div className="text-gray-400 text-sm">Total Runtime</div>
            <div className="text-2xl font-bold text-purple-400 mt-1">{stats.totalRuntime}ms</div>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700 p-4">
            <div className="text-gray-400 text-sm">Avg Confidence</div>
            <div className="text-2xl font-bold text-green-400 mt-1">{stats.avgConfidence.toFixed(1)}%</div>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700 p-4">
            <div className="text-gray-400 text-sm">Successful Modules</div>
            <div className="text-2xl font-bold text-green-400 mt-1">{stats.successfulModules}</div>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700 p-4">
            <div className="text-gray-400 text-sm">Failed Modules</div>
            <div className="text-2xl font-bold text-red-400 mt-1">{stats.failedModules}</div>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700 p-4">
            <div className="text-gray-400 text-sm">Last Simulation</div>
            <div className="text-sm font-medium text-gray-300 mt-1">
              {stats.lastSimTime ? new Date(stats.lastSimTime).toLocaleTimeString() : "N/A"}
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <h2 className="text-xl font-bold mb-4">Run Simulation</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => runSimulation("full")}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Run Full Simulation
            </Button>
            
            <Button
              onClick={() => runSimulation("top")}
              disabled={loading}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-700"
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Run for Top 20 Stocks
            </Button>
            
            <Button
              onClick={() => fetchSimulations()}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-700"
            >
              <History className="mr-2 h-4 w-4" />
              Refresh History
            </Button>
          </div>
        </Card>

        {/* Current Simulation Results */}
        {currentSimulation && (
          <Card className="bg-gray-800/50 border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Latest Simulation Results</h2>
              <Button
                onClick={() => exportToExcel(currentSimulation.simulation_id)}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="mr-2 h-4 w-4" />
                Export Excel
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <div className="text-gray-400 text-sm">Simulation ID</div>
                <div className="text-sm font-mono text-gray-300 mt-1">{currentSimulation.simulation_id?.slice(0, 8)}...</div>
              </div>
              <div>
                <div className="text-gray-400 text-sm">Stocks Analyzed</div>
                <div className="text-lg font-bold text-blue-400 mt-1">{currentSimulation.total_stocks}</div>
              </div>
              <div>
                <div className="text-gray-400 text-sm">Runtime</div>
                <div className="text-lg font-bold text-purple-400 mt-1">{currentSimulation.total_runtime_ms}ms</div>
              </div>
              <div>
                <div className="text-gray-400 text-sm">Avg Confidence</div>
                <div className="text-lg font-bold text-green-400 mt-1">{currentSimulation.avg_confidence?.toFixed(1)}%</div>
              </div>
            </div>

            {/* Stock Results */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-300">Stock Results</h3>


      {/* Module Details Modal */}
      <Dialog open={!!selectedModule} onOpenChange={() => setSelectedModule(null)}>
        <DialogContent className="max-w-3xl bg-gray-900 border-gray-700 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Eye className="h-5 w-5 text-purple-400" />
              Detailed Calculation Breakdown: {selectedModule?.module_name}
            </DialogTitle>
          </DialogHeader>

          {selectedModule && (
            <div className="space-y-6">
              {/* Summary */}
              <Card className="bg-gray-800/50 border-gray-700 p-4">
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-gray-400">Module</div>
                    <div className="text-sm font-bold text-white">{selectedModule.module_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Weight</div>
                    <div className="text-sm font-bold text-blue-400">{selectedModule.module_weight}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Raw Score</div>
                    <div className="text-sm font-bold text-purple-400">{selectedModule.raw_score?.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Weighted Score</div>
                    <div className="text-sm font-bold text-green-400">{selectedModule.weighted_score?.toFixed(4)}</div>
                  </div>
                </div>
              </Card>

              {/* Data Source */}
              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-2">📊 Where This Data Comes From</h3>
                <Card className="bg-gray-800/50 border-gray-700 p-4 space-y-3">
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Data Source:</div>
                    <div className="text-sm text-gray-200">{selectedModule.data_source}</div>
                  </div>
                  {selectedModule.external_api && (
                    <div>
                      <div className="text-xs text-gray-400 mb-1">Website:</div>
                      <div className="text-sm text-blue-400 font-medium">{selectedModule.external_api}</div>
                    </div>
                  )}
                </Card>
              </div>

              {/* Input Data */}
              {selectedModule.input_data && Object.keys(selectedModule.input_data).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-300 mb-2">Input Data Used</h3>
                  <Card className="bg-gray-800/50 border-gray-700 p-3">
                    <div className="space-y-2">
                      {Object.entries(selectedModule.input_data).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-xs text-gray-400">{key}:</span>
                          <span className="text-xs text-gray-200 font-mono">{JSON.stringify(value)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              )}

              {/* Raw Values */}
              {selectedModule.raw_values && Object.keys(selectedModule.raw_values).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-300 mb-2">Raw Values Retrieved</h3>
                  <Card className="bg-gray-800/50 border-gray-700 p-3">
                    <div className="space-y-2">
                      {Object.entries(selectedModule.raw_values).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-xs text-gray-400">{key}:</span>
                          <span className="text-xs text-purple-300 font-mono">{JSON.stringify(value)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              )}

              {/* Calculation Formula */}
              {selectedModule.calculation && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-300 mb-2">Calculation Formula</h3>
                  <Card className="bg-gray-800/50 border-gray-700 p-3">
                    <code className="text-xs text-green-300 font-mono">{selectedModule.calculation}</code>
                  </Card>
                </div>
              )}

              {/* Step-by-Step Breakdown */}
              {selectedModule.calculation_steps && selectedModule.calculation_steps.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-300 mb-2">📋 How The Score Was Calculated</h3>
                  <Card className="bg-gray-800/50 border-gray-700 p-4">
                    <div className="space-y-2">
                      {selectedModule.calculation_steps.map((step, idx) => (
                        <div key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                          <span className="text-blue-400 font-mono text-xs mt-0.5">{idx + 1}.</span>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              )}

              {/* API Response */}
              {selectedModule.api_response && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-300 mb-2">API Response</h3>
                  <Card className="bg-gray-800/50 border-gray-700 p-3">
                    <pre className="text-xs text-gray-300 overflow-x-auto">
                      {JSON.stringify(selectedModule.api_response, null, 2)}
                    </pre>
                  </Card>
                </div>
              )}

              {/* Runtime */}
              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-2">Execution Time</h3>
                <Card className="bg-gray-800/50 border-gray-700 p-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-400" />
                    <span className="text-sm text-gray-300">{selectedModule.runtime_ms}ms</span>
                  </div>
                </Card>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

              {currentSimulation.results?.map((stock, idx) => (
                <Card key={idx} className="bg-gray-900/50 border-gray-700">
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-800/30 transition-colors"
                    onClick={() => toggleStockDetails(stock.stock_symbol)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div>
                          <div className="font-bold text-white">{stock.stock_symbol}</div>
                          <div className="text-sm text-gray-400">{stock.sector}</div>
                        </div>
                        {stock.strategy && (
                          <Badge className={stock.strategy === "BUY" ? "bg-green-600" : "bg-red-600"}>
                            {stock.strategy}
                          </Badge>
                        )}
                        {stock.ranking_score !== undefined && (
                          <div className="text-xs text-gray-400">
                            Rank Score: <span className="text-yellow-400 font-mono">{stock.ranking_score?.toFixed(2)}</span>
                          </div>
                        )}
                        <Badge className="bg-blue-600">
                          {stock.final_confidence?.toFixed(1)}% Confidence
                        </Badge>
                        <div className="text-sm text-gray-400">
                          <Clock className="inline h-3 w-3 mr-1" />
                          {stock.total_runtime_ms}ms
                        </div>
                      </div>
                      {expandedStock === stock.stock_symbol ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Module Details (Expanded) */}
                  {expandedStock === stock.stock_symbol && (
                    <div className="border-t border-gray-700 p-4">
                      <h4 className="text-sm font-semibold text-gray-300 mb-3">Module Execution Details</h4>
                      <div className="space-y-2">
                        {stock.module_logs?.map((module, midx) => (
                          <div
                            key={midx}
                            className="flex items-center justify-between p-3 rounded bg-gray-800/30 border border-gray-700/50"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <div className="text-xs font-mono text-gray-500">#{module.module_sequence}</div>
                              {module.status === "success" ? (
                                <CheckCircle2 className="h-4 w-4 text-green-400" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-400" />
                              )}
                              <div className="flex-1">
                                <div className="text-sm font-medium text-white">{module.module_name}</div>
                                <div className="text-xs text-gray-500">{module.data_source}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <div>
                                <span className="text-gray-400">Weight:</span>
                                <span className="text-blue-400 ml-1 font-mono">{module.module_weight}</span>
                              </div>
                              <div>
                                <span className="text-gray-400">Raw:</span>
                                <span className="text-purple-400 ml-1 font-mono">{module.raw_score?.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-400">Weighted:</span>
                                <span className="text-green-400 ml-1 font-mono">{module.weighted_score?.toFixed(4)}</span>
                              </div>
                              <div className="text-gray-500 text-xs">
                                <Clock className="inline h-3 w-3 mr-1" />
                                {module.runtime_ms}ms
                              </div>
                              <Button
                                onClick={() => setSelectedModule(module)}
                                size="sm"
                                variant="outline"
                                className="border-purple-700 text-purple-400 hover:bg-purple-900/20"
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                Details
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Simulation History */}
        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <h2 className="text-xl font-bold mb-4">Simulation History</h2>
          <div className="space-y-3">
            {simulations.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No simulations found. Run your first simulation above.</p>
              </div>
            ) : (
              simulations.map((sim, idx) => (
                <Card key={idx} className="bg-gray-900/50 border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-mono text-sm text-gray-400">
                          {sim.simulation_id?.slice(0, 8)}...
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(sim.start_time).toLocaleString()}
                        </div>
                      </div>
                      <Badge variant={sim.status === "completed" ? "default" : "destructive"}>
                        {sim.status}
                      </Badge>
                      <div className="text-sm text-gray-400">
                        {sim.total_stocks} stocks • {sim.total_runtime_ms}ms • {sim.avg_confidence?.toFixed(1)}% conf
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => viewSimulationDetails(sim.simulation_id)}
                        size="sm"
                        variant="outline"
                        className="border-gray-700"
                      >
                        View Details
                      </Button>
                      <Button
                        onClick={() => exportToExcel(sim.simulation_id)}
                        size="sm"
                        variant="outline"
                        className="border-gray-700"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
